import { ArrowUpDown, Eye, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Recipe } from '../../../types/recipe';
import type { RecipeDisplayEntry, RecipeSortKey } from './types';

interface SortableHeaderProps {
  column: RecipeSortKey;
  label: string;
  activeKey?: RecipeSortKey;
  onSort: (key: RecipeSortKey) => void;
}

function SortableHeader({ column, label, activeKey, onSort }: SortableHeaderProps) {
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-2">
        {label}
        {activeKey === column && <ArrowUpDown className="h-4 w-4" />}
      </div>
    </th>
  );
}

interface RecipesTableProps {
  entries: RecipeDisplayEntry[];
  sortKey?: RecipeSortKey;
  onSort: (key: RecipeSortKey) => void;
  canWrite: boolean;
  canDelete: boolean;
  deletingRecipeId: string | null;
  onRowClick: (recipe: Recipe) => void;
  onDelete: (recipe: Recipe) => void;
}

/** Desktop table of recipes with sortable columns and per-row view/delete actions. */
export function RecipesTable({
  entries,
  sortKey,
  onSort,
  canWrite,
  canDelete,
  deletingRecipeId,
  onRowClick,
  onDelete,
}: RecipesTableProps) {
  const { t } = useLanguage();

  return (
    <div className="hidden md:block bg-white shadow overflow-hidden rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader column="name" label={t('recipes.form.recipeName')} activeKey={sortKey} onSort={onSort} />
            <SortableHeader column="itemCodeDisplay" label={t('recipes.page.columns.itemCode')} activeKey={sortKey} onSort={onSort} />
            <SortableHeader column="roastTypeDisplay" label={t('workOrders.table.roastType')} activeKey={sortKey} onSort={onSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('roast.formHeader.machine')}
            </th>
            <SortableHeader column="targetBeanWeight" label={t('recipes.page.columns.targetWeightInOut')} activeKey={sortKey} onSort={onSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.details.notes')}</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settingsMcp.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {entries.map((entry) => {
            const recipe = entry.recipe;
            return (
              <tr
                key={recipe.id}
                data-test-id={`recipe-row-${recipe.id}`}
                className={`hover:bg-gray-50 transition-colors ${canWrite ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => onRowClick(recipe)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {recipe.name}
                  {recipe.tastingNotes && (
                    <div className="absolute left-1/4 top-full mt-1 z-50 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-gray-700 pointer-events-none transform -translate-x-1/2">
                      <div className="flex items-center gap-2 mb-1 border-b border-gray-700 pb-1">
                        <span className="font-semibold text-yellow-400">{t('recipes.tastingSection.title')}</span>
                      </div>
                      <div className="space-y-1">
                        {recipe.tastingNotes.notes && <p className="leading-relaxed text-gray-300 italic">"{recipe.tastingNotes.notes}"</p>}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {recipe.tastingNotes.flavorTags.slice(0, 5).map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 text-[10px]">
                              {tag}
                            </span>
                          ))}
                          {recipe.tastingNotes.flavorTags.length > 5 && (
                            <span className="px-1.5 py-0.5 text-gray-400 text-[10px]">
                              {t('recipes.page.moreCount', { count: recipe.tastingNotes.flavorTags.length - 5 })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">{entry.itemNameDisplay}</span>
                    <span className="text-xs text-gray-400">{entry.itemCodeDisplay}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">{entry.roastTypeDisplay}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.machineDisplay}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    {t('inventory.roastedBulk.in')}: {recipe.targetBeanWeight} {t('inventory.history.unitKg')}
                  </div>
                  {recipe.targetWeightOutput && (
                    <div className="text-xs text-gray-400">
                      {t('inventory.roastedBulk.out')}: {recipe.targetWeightOutput} {t('inventory.history.unitKg')}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{recipe.internalNotes || t('settingsMcp.values.none')}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      {canWrite ? t('reports.shift.view') : t('common.view')}
                    </button>

                    {canDelete && (
                      <button
                        type="button"
                        data-test-id={`recipe-delete-button-${recipe.id}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(recipe);
                        }}
                        disabled={deletingRecipeId === recipe.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {deletingRecipeId === recipe.id ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            {t('recipes.page.actions.deleting')}
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            {t('purchasing.supplierList.actions.delete')}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
